import {
  ClientEventDto,
  ClientEventParticipantDto,
  ClientEventParticipantUserDto,
  EventEntity,
  EventLanguageTypeEnum,
  EventTypeEnum,
  EventVariantEntity,
  ThemeEntity,
  UserEntity,
} from "@citychallenge/api-public";
import {
  IsNotEmpty,
  IsDateString,
  IsOptional,
  IsNumber,
  IsString,
  validateSync,
  validateOrReject,
  IsBoolean,
  IsUrl,
} from "class-validator";
import { IsValidMarkdown } from "./validators";
import {
  isSessionOver,
  sessionRunsUntil,
  sessionWindow,
  Types,
} from "@citychallenge/common";
import dayjs from "dayjs";
import { observable, action, computed } from "mobx";
import { LocalizedMoment } from "../i18n";
import { MarkdownParser } from "../markdown";

/**
 * Whether a project publishing `languages` runs a session in this one. An empty
 * set means the project's languages are still loading: offer everything rather
 * than nothing.
 */
function offersLanguage(
  language: EventLanguageTypeEnum | undefined,
  languages: string[] | undefined
) {
  return !languages?.length || !language || languages.includes(language);
}

/** The event's own date, as opposed to one of its variants. */
export const MAIN_SESSION_ID = "main";

export interface EventSession {
  /** {@link MAIN_SESSION_ID} for the event's own date, else the variant id. */
  id: string;
  startDate: string;
  eventLanguage?: EventLanguageTypeEnum;
}

interface ObservableUserStore {
  user?: UserEntity;
}

/**
 * A row of an event's participant list. Only the caller's own rows carry a
 * `user`. Another user's confirmed row is an anonymous marker that counts as
 * a taken place, and other users' waiting-list rows are never sent (see
 * `redactForeignParticipants` on the server).
 */
export type EventParticipationState =
  | "waiting-list"
  | "finished"
  | "in-progress"
  | "starts-soon"
  | "attending";

/** The dates a participation reads its session from. */
interface SessionHost {
  startDate: string;
  endDate?: string;
  link?: string;
}

export class EventParticipantModel {
  public user?: ClientEventParticipantUserDto;
  public created?: string;

  public confirmed?: string;

  public demioJoinUrl?: string;

  public webinarGeekJoinUrl?: string;

  public variant?: EventVariantEntity;

  constructor(
    data: ClientEventParticipantDto,
    private userStore: ObservableUserStore,
    private host: SessionHost = { startDate: "" }
  ) {
    Object.assign(this, data);
  }

  @computed
  public get activeVariantId() {
    return this.variant?.id || MAIN_SESSION_ID;
  }

  /** The session this registration is for: {@link MAIN_SESSION_ID} or a variant id. */
  public get sessionId() {
    return this.activeVariantId;
  }

  @computed
  public get isCurrentUser() {
    const userId = this.userStore.user?.id;
    return !!userId && this.user?.id === userId;
  }

  @computed
  public get startDate(): string {
    return this.variant?.startDate || this.host.startDate;
  }

  @computed
  public get endDate(): string | undefined {
    const { variant } = this;
    return variant ? variant.endDate ?? variant.startDate : this.host.endDate;
  }

  @computed
  public get isWaiting() {
    return !this.confirmed;
  }

  @computed
  public get joinUrl() {
    return this.webinarGeekJoinUrl || this.demioJoinUrl || this.host.link;
  }

  // Clock-dependent: plain getters, a cached value would go stale.
  public get isOver() {
    return isSessionOver(sessionWindow(this.host, this.variant));
  }

  public get inProgress() {
    return !this.isOver && dayjs().isAfter(dayjs(this.startDate));
  }

  public get startsSoon() {
    if (this.isOver || this.inProgress) return false;
    return dayjs(this.startDate).isBefore(dayjs().add(2, "hour"));
  }

  public get state(): EventParticipationState {
    if (this.isWaiting) return "waiting-list";
    if (this.isOver) return "finished";
    if (this.inProgress) return "in-progress";
    if (this.startsSoon) return "starts-soon";
    return "attending";
  }
}

export class EventModel
  implements Omit<EventModel, "participants" | "division">
{
  public static create(
    defaults: Partial<EventEntity> = {}
  ): Partial<EventModel> {
    return new EventModel(defaults as EventEntity, {});
  }
  public id!: string;

  @IsNotEmpty()
  @observable
  public name!: string;

  @IsDateString()
  @observable
  public startDate!: string;

  @IsNumber()
  @IsOptional()
  @observable
  public maxParticipants!: number;

  @IsDateString()
  @IsOptional()
  @observable
  public endDate?: string;

  @IsValidMarkdown()
  @observable
  public description?: Types["MarkdownText"];

  @observable
  public eventLanguage?: EventLanguageTypeEnum = EventLanguageTypeEnum.Nl;

  @IsString()
  @IsOptional()
  @observable
  public phone?: string;

  @IsString()
  @IsOptional()
  @observable
  public location?: string;

  @IsOptional()
  @IsBoolean()
  @observable
  public hideParticipants?: boolean;

  @IsOptional()
  @IsUrl()
  @observable
  public link?: string;

  @IsNotEmpty()
  @IsOptional()
  @observable
  public image?: string;

  @IsOptional()
  @IsBoolean()
  @observable
  public eventIsVisibleToUser?: string;

  @observable
  public theme?: ThemeEntity;

  @observable
  public variants?: EventVariantEntity[];

  public user?: UserEntity;

  public demioEventId?: string;

  public webinarGeekBroadcastId?: string;

  @observable
  public eventType?: EventTypeEnum = EventTypeEnum.Webinar;

  @computed
  public get isValid() {
    const errors = validateSync(this);
    return !errors.length;
  }

  public async getValidatedObject() {
    await validateOrReject(this);

    return {
      name: this.name,
      image: this.image,
      startDate: this.startDate,
      maxParticipants: this.maxParticipants,
      description: this.description,
      phone: this.phone,
      location: this.location,
      hideParticipants: this.hideParticipants,
    };
  }

  @observable
  public participants: EventParticipantModel[] = [];

  @action
  public setParticipants(participants: ClientEventParticipantDto[]) {
    this.participants = participants.map((data) => {
      return new EventParticipantModel(data, this.userStore, this);
    });
  }

  @computed
  public get startDateOptions() {
    return [
      this.startDate,
      ...(this.variants ?? []).map((variant) => variant.startDate),
    ]
      .filter(Boolean)
      .sort();
  }

  /**
   * The sessions a project actually runs, ascending. A webinar offers one
   * session per language, so a project that doesn't publish a language never
   * offers its sessions — not for signing up, and not as the date to show.
   * An empty language set means they're still loading: offer everything rather
   * than nothing.
   *
   * The pages runtime's single source for what is on offer — its cards,
   * calendar and register modal all read this, so a picker cannot drift from
   * a card. The older shared selector
   * (`web/src/events/event-webinar-subscribe-selector.tsx`) still builds and
   * language-filters its own list; migrating it here would retire the last
   * copy of this policy.
   */
  public sessionsIn(languages?: string[]): EventSession[] {
    return [
      {
        id: MAIN_SESSION_ID,
        startDate: this.startDate,
        eventLanguage: this.eventLanguage,
      },
      ...(this.variants ?? []).map((variant) => ({
        id: variant.id,
        startDate: variant.startDate,
        eventLanguage: variant.eventLanguage,
      })),
    ]
      .filter(
        (session) =>
          !!session.startDate &&
          offersLanguage(session.eventLanguage, languages)
      )
      .sort((a, b) => a.startDate.localeCompare(b.startDate));
  }

  /** The dates of {@link sessionsIn}, for callers that only need the when. */
  public startDateOptionsIn(languages?: string[]) {
    return this.sessionsIn(languages).map(({ startDate }) => startDate);
  }

  /** The extra sessions on offer — more than none means the visitor picks one. */
  public variantsIn(languages?: string[]) {
    return (this.variants ?? []).filter((variant) =>
      offersLanguage(variant.eventLanguage, languages)
    );
  }

  @computed
  public get highestStartDate() {
    const options = this.startDateOptions;
    return options[options.length - 1] ?? this.startDate;
  }

  /** Not `@computed`: a cached clock-dependent value goes stale as time passes. */
  public get nextStartDate() {
    const now = Date.now();
    return (
      this.startDateOptions.find((start) => Date.parse(start) > now) ??
      this.highestStartDate
    );
  }

  /** Whether the registration that counts right now is still on the waiting list. */
  public get isOnWaitingList() {
    return !!this.participation?.isWaiting;
  }

  @computed
  public get spotsLeft() {
    if (!this.maxParticipants) return -1;

    const left = this.maxParticipants - this.confirmedParticipants.length;
    if (left < 0) {
      return 0;
    } else {
      return left;
    }
  }

  @computed
  public get confirmedParticipants() {
    return this.participants.filter((p) => !!p.confirmed);
  }

  @computed
  public get waitingParticipants() {
    return this.participants.filter((p) => !p.confirmed);
  }

  /** The visitor's own registrations, soonest session first. */
  @computed
  public get participations() {
    return this.participants
      .filter((p) => p.isCurrentUser)
      .sort((a, b) => a.startDate.localeCompare(b.startDate));
  }

  /**
   * The registration that counts right now: the one whose session is still
   * to come or running (a visitor holds at most one), else the latest one
   * they attended. Everything that speaks of "the" participation reads this.
   */
  public get activeParticipation(): EventParticipantModel | undefined {
    const { participations } = this;
    return (
      participations.find((p) => !p.isOver) ??
      participations[participations.length - 1]
    );
  }

  public get participation() {
    return this.activeParticipation;
  }

  /** Holds a registration whose session is not over. */
  public get hasUpcomingParticipation() {
    return !!this.activeParticipation && !this.activeParticipation.isOver;
  }

  public participationFor(sessionId: string) {
    return this.participations.find((p) => p.sessionId === sessionId);
  }

  public sessionState(sessionId: string) {
    return this.participationFor(sessionId)?.state;
  }

  /** A session to move the upcoming registration to, rather than add. */
  public canChangeTo(sessionId: string) {
    return this.hasUpcomingParticipation && !this.participationFor(sessionId);
  }

  public get participationStartDate() {
    return this.participation?.startDate || this.startDate;
  }

  public get startsSoon() {
    if (this.inProgress || this.inPast) return false;
    return dayjs(this.participationStartDate).isBefore(dayjs().add(2, "hour"));
  }

  public get participationEndDate() {
    return this.participation ? this.participation.endDate : this.endDate;
  }

  /**
   * Without the end date an event running for a week would count as finished on
   * its first afternoon, while the lists — which cut off on it — still offer it.
   */
  private get runsUntil() {
    return dayjs(
      sessionRunsUntil({
        startDate: this.participationStartDate,
        endDate: this.participationEndDate,
      })
    );
  }

  public get inProgress() {
    return (
      dayjs().isBefore(this.runsUntil) &&
      dayjs().isAfter(dayjs(this.participationStartDate))
    );
  }

  public get inPast() {
    return dayjs().isAfter(this.runsUntil);
  }

  public get canSubscribeToRegularEvent() {
    return !dayjs().isAfter(this.startDate);
  }

  public get eventState() {
    if (this.inPast) return "in-past";
    if (this.inProgress) return "in-progress";
    if (this.startsSoon) return "starts-soon";
    return "upcoming";
  }

  public get participationStartDateLocalized() {
    return LocalizedMoment.create(this.participationStartDate);
  }

  @computed
  public get isParticipating() {
    return this.participations.length > 0;
  }

  constructor(data: ClientEventDto, private userStore: ObservableUserStore) {
    Object.assign(this, data);
    this.setParticipants(data.participants || []);
  }

  public runningInHotlink?: boolean;

  @computed
  public get startDateLocalized() {
    if (!this.startDate) return undefined;
    return new LocalizedMoment(this.startDate);
  }

  @computed
  public get endDateLocalized() {
    if (!this.endDate) return undefined;
    return new LocalizedMoment(this.endDate);
  }

  @computed
  public get endDateOrTimeReadable() {
    if (!this.startDate && !this.endDate) return undefined;

    const sameDay = dayjs(this.endDate)
      .startOf("day")
      .isSame(dayjs(this.startDate).startOf("day"));

    if (sameDay) return dayjs(this.endDate).format("HH:mm");
    return this.endDateLocalized?.dayAndMonth;
  }

  @computed
  public get duration() {
    if (!this.endDate) return undefined;

    return Math.ceil(
      dayjs.duration(dayjs(this.endDate).diff(this.startDate)).asMinutes()
    );
  }

  @computed
  public get preview() {
    return MarkdownParser.parsePreview(this.description?.text);
  }

  /** Nothing to join while the place is not confirmed, whatever link the event has. */
  public get joinUrl() {
    const { participation } = this;
    if (participation?.isWaiting) return undefined;
    return participation?.joinUrl ?? this.link;
  }

  public contains(regex: RegExp) {
    const { name, description } = this;
    const { text = "" } = description || {};

    return regex.test(name) || regex.test(text);
  }
}
